import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useAppSelector } from "../../hooks"
import { updateIsAllowNewServiceCategory } from "../../redux/slice/auth"
import moment from "moment"
import { langWithCurrentMomentCode, servicesBgList } from "../../utils/helpers"
import useWindowSize from "../../hooks/useWindowSize"
import { ICategory, IProject } from "../../types/content"
import clsx from "clsx"
import CategoryItem from "../CategoryItem/CategoryItem"
import { getLocale } from "../../utils/getLocale"
import styles from "./ServicesList.module.scss"
import { DEFAULT_LANGUAGE } from "../../utils/constants"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { setSoloServiceIsOpened } from "../../redux/slice/main"
import { useGetProjectsQuery, useLazyGetProjectsQuery } from "../../redux/api/main"

interface IItem {
  bg: string
  id: string
  image_id?: string
  is_allow_prorose_new_service?: boolean
  avatar_id?: null | string
  description: null | string
  isCurrent?: boolean
  name: string | null
  project_id?: string | null
}

interface Props {
  isProjects?: boolean
}

const ServicesList = ({ isProjects }: Props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const dispatch = useDispatch()
  const currentLang = getLocale()
  const { object_id } = useParams()
  // Получаем проекты на Главной '/'
  const [getProjects, { data: projectsList, isLoading: isLoadingProjects }] = useLazyGetProjectsQuery()

  const { serviceList, serviceListLoading, soloServiceIsOpened } = useAppSelector((state) => state.main)

  useEffect(() => {
    if (!isProjects || Boolean(object_id)) return
    getProjects()
  }, [isProjects, object_id])

  useEffect(() => {
    if (currentLang) {
      moment.locale(langWithCurrentMomentCode.get(currentLang))
    } else {
      moment.locale(DEFAULT_LANGUAGE)
    }
  }, [currentLang])

  useEffect(() => {
    dispatch(updateIsAllowNewServiceCategory(false))

    // Открытие категории, если в списке она одна
    if (object_id && serviceList?.length === 1 && !soloServiceIsOpened) {
      dispatch(setSoloServiceIsOpened(true))
      const service = serviceList[0]
      const prefix = location.pathname.includes("project")
        ? location.pathname
        : //@ts-ignore
        service?.project_id
        ? //@ts-ignore
          `${location.pathname}/project/${service.project_id}`
        : location.pathname
      navigate(`${prefix}/category/${serviceList[0].id}`)
    }
  }, [])

  const { isDesktop } = useWindowSize()

  const list = useMemo(() => {
    if (!isProjects && !serviceList?.length) return null
    if (isProjects && !projectsList?.projects?.length) return null

    let isFive = true
    const projects = projectsList?.projects || []
    const currentServiceList = isProjects ? projects : serviceList
    const result = (currentServiceList as IItem[]).reduce((accumulator: IItem[][], currentValue, index) => {
      if (!isDesktop) {
        if (index % 4 === 0) accumulator.push([])
      } else {
        if (isFive) {
          if (index === 0) accumulator.push([])
          if ((index + 3) % 8 === 0) {
            accumulator.push([])
            if (index !== 0) isFive = false
          }
        } else {
          if (index % 8 === 0) {
            accumulator.push([])
            isFive = true
          }
        }
      }

      accumulator[accumulator.length - 1].push({ ...currentValue, bg: servicesBgList[index % 12] })
      return accumulator
    }, [])

    let sufNum5 = 0
    let sufNum3 = 0
    return result.map((item, index) => {
      const num = index % 2 === 0 ? 5 : 3
      let fullNum = null
      if (isDesktop) {
        if (num === 5) {
          sufNum5 = sufNum5 >= 3 ? 1 : ++sufNum5
        }
        if (num === 3) {
          sufNum3 = sufNum3 >= 3 ? 1 : ++sufNum3
        }
        fullNum = num + "-" + (num === 5 ? sufNum5 : sufNum3)
      }

      let lastClass = null
      if (index + 1 === result.length) {
        lastClass = styles["row--last-" + (isDesktop ? "" : "mob-") + item.length]
      }

      return (
        <div
          key={index}
          className={clsx(
            styles.row,
            !isDesktop && styles.mob,
            !lastClass && fullNum && styles[`row--${fullNum}`],
            lastClass,
          )}
        >
          {item?.map((el) => {
            return (
              <CategoryItem
                key={el.id}
                id={el.id}
                title={el?.name}
                subtitle={el?.description}
                background={el.bg}
                img={el?.image_id || el?.avatar_id || undefined}
                className={styles.item}
                isProject={isProjects}
                projectID={el?.project_id}
              />
            )
          })}
        </div>
      )
    })
  }, [serviceList, projectsList, isDesktop, isProjects])

  return (
    <div className={styles.serviceList}>
      {isDesktop && (
        <h4
          className={clsx(
            styles.serviceList__title,
            isLoadingProjects && isProjects && `selectBack ${styles.serviceList__title_load}`,
            serviceListLoading && !isProjects && `selectBack ${styles.serviceList__title_load}`,
          )}
        >
          {t(isProjects ? "projects" : "services")}
        </h4>
      )}

      <div className={styles.serviceList__list}>
        {(isProjects && isLoadingProjects) || (!isProjects && serviceListLoading && !serviceList?.length) ? (
          <div className={styles.gridLoading}>
            <div className={`selectBack ${styles.elLoading}`} />
            <div className={`selectBack ${styles.elLoading}`} />
            <div className={`selectBack ${styles.elLoading}`} />
            <div className={`selectBack ${styles.elLoading}`} />
            <div className={`selectBack ${styles.elLoading}`} />
          </div>
        ) : (
          <div className={styles.grid}>{list}</div>
        )}
      </div>
    </div>
  )
}

export default ServicesList
